import React, { useState, useEffect } from 'react';
import { getVendorApplications, getVendorApplicationDetails, updateVendorStatus, deleteVendorApplication } from '../api/api';
import Comments from '../components/Comments';
import StatusHistory from '../components/StatusHistory';
import './AdminVendors.css';

function AdminVendors() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedVendorImage, setSelectedVendorImage] = useState(null);
  const [showVendorImageViewer, setShowVendorImageViewer] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // Download individual application as Excel
  const handleDownloadExcel = async (id, refNumber) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(`/api/vendors/${id}/download-excel`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Excel download failed:', errorText);
        throw new Error(`Failed to download Excel: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error('Received empty file');
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vendor-application-${refNumber}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      alert('Error downloading Excel file: ' + error.message);
    }
  };

  // Download all applications as Excel
  const handleDownloadAllExcel = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch('/api/vendors/download-excel', {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Excel download failed:', errorText);
        throw new Error(`Failed to download Excel: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error('Received empty file');
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'vendor-applications.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      alert('Error downloading Excel file: ' + error.message);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching vendor applications...');
      const response = await getVendorApplications({ limit: 50 });
      console.log('Vendor applications response:', response.data);
      // Normalize: ensure _id is always set (Firestore returns 'id', MongoDB returns '_id')
      const apps = (response.data.applications || response.data || []).map(app => ({
        ...app,
        _id: app._id || app.id
      }));
      setApplications(apps);
      console.log('Applications set:', apps.length);
    } catch (error) {
      console.error('=== VENDOR APPLICATIONS FETCH ERROR ===');
      console.error('Error:', error);
      console.error('Response:', error.response);
      console.error('Status:', error.response?.status);
      console.error('Data:', error.response?.data);
      setError(`Failed to load vendor applications: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (id) => {
    setLoadingDetails(true);
    setShowDetailsModal(true);
    try {
      const response = await getVendorApplicationDetails(id);
      setSelectedApplication(response.data.application);
    } catch (error) {
      console.error('Error fetching vendor details:', error);
      alert('Error loading vendor details: ' + error.message);
      setShowDetailsModal(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedApplication(null);
    setActiveTab('details');
  };

  const handleViewVendorImage = (imageUrl, productName, appNumber) => {
    setSelectedVendorImage({ 
      url: imageUrl, 
      productName, 
      appNumber,
      alt: `${productName} - ${appNumber}`
    });
    setShowVendorImageViewer(true);
  };

  const closeVendorImageViewer = () => {
    setShowVendorImageViewer(false);
    setSelectedVendorImage(null);
  };

  const handleDownloadVendorImage = () => {
    if (selectedVendorImage?.url) {
      const link = document.createElement('a');
      link.href = selectedVendorImage.url;
      link.download = `${selectedVendorImage.productName || 'image'}-${selectedVendorImage.appNumber || 'vendor'}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    // Ask for a reason/notes
    const reason = prompt(`Enter ${status === 'approved' ? 'approval' : 'rejection'} reason (optional):`, '');
    
    if (reason === null) {
      // User cancelled
      return;
    }

    try {
      await updateVendorStatus(id, { 
        status, 
        notes: reason || ''
      });
      alert('Status updated successfully');
      fetchApplications();
    } catch (error) {
      alert('Error updating status: ' + error.message);
    }
  };

  const handleDeleteApplication = async (application) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this vendor application?\n\n` +
      `Application #: ${application.applicationNumber}\n` +
      `Business: ${application.businessDetails?.businessName || 'N/A'}\n` +
      `Contact: ${application.contactInfo?.firstName} ${application.contactInfo?.lastName}\n` +
      `Status: ${application.status}\n\n` +
      `This will download an Excel file with the application details and permanently remove it from the system.`
    );

    if (!confirmed) return;

    try {
      const response = await deleteVendorApplication(application._id);
      
      // Download the Excel file
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `deleted-vendor-application-${application.applicationNumber}-${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert('Vendor application deleted successfully. Excel file has been downloaded.');
      fetchApplications();
    } catch (error) {
      alert('Error deleting application: ' + (error.response?.data?.message || error.message));
      console.error(error);
    }
  };

  const handleDownload = async (id, refNumber) => {
    try {
      const application = applications.find(app => app._id === id);
      if (!application) {
        alert('Application not found');
        return;
      }

      // Collect all images from products (imageUrls = Firebase Storage URLs, images = base64/legacy)
      const allImages = [];
      application.products.forEach((product, prodIdx) => {
        const imgs = [...(product.imageUrls || []), ...(product.images || [])];
        imgs.forEach((img, imgIdx) => {
          const isBase64 = typeof img === 'string' && img.startsWith('data:');
          allImages.push({
            url: img,
            productName: product.name,
            productIndex: prodIdx,
            imageIndex: imgIdx,
            isBase64
          });
        });
      });

      if (allImages.length === 0) {
        alert('No images found for this application');
        return;
      }

      // Download base64 images by converting to blob
      allImages.forEach((img, idx) => {
        setTimeout(() => {
          try {
            if (img.isBase64) {
              // Handle base64 data URL
              fetch(img.url)
                .then(res => res.blob())
                .then(blob => {
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  // Extract file extension from data URL
                  const ext = img.url.match(/data:image\/(.*?);/)?.[1] || 'jpg';
                  a.download = `${refNumber}-${img.productName.replace(/\s+/g, '-')}-${img.imageIndex + 1}.${ext}`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                });
            } else {
              // Handle regular URL
              const a = document.createElement('a');
              a.href = img.url;
              const ext = img.url.split('.').pop().split('?')[0] || 'jpg';
              a.download = `${refNumber}-${img.productName.replace(/\s+/g, '-')}-${img.imageIndex + 1}.${ext}`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }
          } catch (err) {
            console.error('Error downloading image:', err);
          }
        }, idx * 500); // Stagger downloads
      });

      alert(`Started downloading ${allImages.length} images...`);
    } catch (error) {
      console.error('Error downloading images:', error);
      alert('Error downloading images: ' + error.message);
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="admin-vendors">
      <div className="vendor-header">
        <h2>Vendor Applications</h2>
        <div className="vendor-count-badge">
          Total: {applications.length} application{applications.length !== 1 ? 's' : ''}
        </div>
      </div>
      <div style={{ marginBottom: '16px' }}>
        <button className="btn btn-primary" onClick={handleDownloadAllExcel}>
          📊 Download All as Excel
        </button>
      </div>
      <div className="vendors-table">
        <table>
          <thead>
            <tr>
              <th>Reference #</th>
              <th>Business Name</th>
              <th>Contact</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {applications.map(app => (
              <React.Fragment key={app._id}>
                <tr>
                  <td><strong>{app.applicationNumber || app.referenceNumber || 'N/A'}</strong></td>
                  <td>{app.businessDetails?.businessName || app.businessName || 'N/A'}</td>
                  <td>
                    {app.contactInfo?.firstName || app.name || 'N/A'} {app.contactInfo?.lastName || ''}<br/>
                    {app.contactInfo?.email || app.email || 'N/A'}
                  </td>
                  <td>
                    <span className={`badge ${app.status}`}>{app.status}</span>
                  </td>
                  <td>{new Date(app.submittedAt).toLocaleString()}</td>
                  <td>
                    <div className="actions">
                      {app.status === 'pending' && (
                        <>
                          <button className="btn-verify" onClick={() => handleStatusUpdate(app._id, 'approved')}>✓ Approve</button>
                          <button className="btn-delete" onClick={() => handleStatusUpdate(app._id, 'rejected')}>✗ Reject</button>
                        </>
                      )}
                      {app.status !== 'pending' && (
                        <span className="status-text">{app.status.toUpperCase()}</span>
                      )}
                      <button 
                        className="btn-primary" 
                        onClick={() => handleViewDetails(app._id)}
                        title="View full application details with all images and documents"
                      >
                        👁️ View Full Details
                      </button>
                      <button 
                        className="btn-view" 
                        onClick={() => handleDownload(app._id, app.applicationNumber || app.referenceNumber)}
                        title="Download all product images"
                      >
                        🖼️ Images
                      </button>
                      <button
                        className="btn-edit"
                        onClick={() => handleDownloadExcel(app._id, app.applicationNumber || app.referenceNumber)}
                        title="Download Excel with product details"
                      >
                        📊 Excel
                      </button>
                      <button
                        className="btn-notes"
                        onClick={() => setExpandedId(expandedId === app._id ? null : app._id)}
                        title="View or add notes"
                      >
                        📝 Notes
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => handleDeleteApplication(app)}
                        title="Delete application and download Excel backup"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedId === app._id && (
                  <tr className="expanded-row">
                    <td colSpan="6">
                      <div className="expanded-content">
                        <StatusHistory statusHistory={app.statusHistory} />
                        <Comments entityType="vendor" entityId={app._id} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Full Details Modal */}
      {showDetailsModal && (
        <div className="modal-overlay" onClick={closeDetailsModal}>
          <div className="modal-content vendor-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Vendor Application Details</h2>
              <button className="close-btn" onClick={closeDetailsModal}>×</button>
            </div>
            
            {loadingDetails ? (
              <div className="loading-details">Loading complete details...</div>
            ) : selectedApplication ? (
              <div className="modal-body">
                {/* Tab Navigation */}
                <div className="tab-navigation">
                  <button 
                    className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
                    onClick={() => setActiveTab('details')}
                  >
                    📋 Details
                  </button>
                  <button 
                    className={`tab-button ${activeTab === 'images' ? 'active' : ''}`}
                    onClick={() => setActiveTab('images')}
                  >
                    🖼️ Images
                  </button>
                  <button 
                    className={`tab-button ${activeTab === 'comments' ? 'active' : ''}`}
                    onClick={() => setActiveTab('comments')}
                  >
                    💬 Comments
                  </button>
                </div>

                {/* Details Tab */}
                {activeTab === 'details' && (
                  <>
                {/* Application Info */}
                <div className="details-section">
                  <h3>Application Information</h3>
                  <p><strong>Reference Number:</strong> {selectedApplication.applicationNumber || selectedApplication.referenceNumber || 'N/A'}</p>
                  <p><strong>Status:</strong> <span className={`badge ${selectedApplication.status}`}>{selectedApplication.status}</span></p>
                  <p><strong>Submitted:</strong> {new Date(selectedApplication.submittedAt).toLocaleString()}</p>
                </div>

                {/* Business Details */}
                <div className="details-section">
                  <h3>Business Details</h3>
                  <p><strong>Business Name:</strong> {selectedApplication.businessDetails?.businessName || 'N/A'}</p>
                  <p><strong>Business Type:</strong> {selectedApplication.businessDetails?.businessType || 'N/A'}</p>
                  <p><strong>Registration Number:</strong> {selectedApplication.businessDetails?.registrationNumber || 'N/A'}</p>
                  <p><strong>Tax Number:</strong> {selectedApplication.businessDetails?.taxNumber || 'N/A'}</p>
                  <p><strong>Years in Business:</strong> {selectedApplication.businessDetails?.yearsInBusiness || 'N/A'}</p>
                  <p><strong>Cultural Specialty:</strong> {selectedApplication.businessDetails?.culturalSpecialty || 'N/A'}</p>
                  <p><strong>Description:</strong> {selectedApplication.businessDetails?.description || 'N/A'}</p>
                  <p><strong>Website:</strong> {selectedApplication.businessDetails?.website ? <a href={selectedApplication.businessDetails.website} target="_blank" rel="noopener noreferrer">{selectedApplication.businessDetails.website}</a> : 'N/A'}</p>
                </div>

                {/* Contact Info */}
                <div className="details-section">
                  <h3>Contact Information</h3>
                  <p><strong>Name:</strong> {selectedApplication.contactInfo?.firstName} {selectedApplication.contactInfo?.lastName}</p>
                  <p><strong>Email:</strong> {selectedApplication.contactInfo?.email}</p>
                  <p><strong>Phone:</strong> {selectedApplication.contactInfo?.phone}</p>
                  <p><strong>Alternate Phone:</strong> {selectedApplication.contactInfo?.alternatePhone || 'N/A'}</p>
                  <p><strong>Address:</strong> {selectedApplication.contactInfo?.address?.street}, {selectedApplication.contactInfo?.address?.city}, {selectedApplication.contactInfo?.address?.province} {selectedApplication.contactInfo?.address?.postalCode}</p>
                </div>

                {/* Products */}
                <div className="details-section">
                  <h3>Products ({selectedApplication.products?.length || 0})</h3>
                  {selectedApplication.products && selectedApplication.products.length > 0 ? (
                    <div className="products-grid">
                      {selectedApplication.products.map((product, idx) => (
                        <div key={idx} className="product-detail-card">
                          <h4>{product.name}</h4>
                          <p><strong>Category:</strong> {product.category}</p>
                          <p><strong>Culture:</strong> {product.culture}</p>
                          <p><strong>Price:</strong> R{product.price}</p>
                          <p><strong>Description:</strong> {product.description || 'N/A'}</p>
                          {product.images && product.images.length > 0 && (
                            <div className="product-images-gallery">
                              <strong>Images ({product.images.length}):</strong>
                              <div className="images-row">
                                {product.images.map((img, imgIdx) => (
                                  <div key={imgIdx} className="image-wrapper">
                                    <img 
                                      src={img} 
                                      alt={`${product.name} ${imgIdx + 1}`} 
                                      className="product-image-thumb"
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => handleViewVendorImage(img, product.name, selectedApplication.applicationNumber)}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAiIGhlaWdodD0iODAiIGZpbGw9IiNmMGYwZjAiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
                                        e.target.style.cursor = 'default';
                                      }}
                                    />
                                    <div className="image-info">
                                      <small>Click to view full size</small>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {/* Debug: Show raw image URLs */}
                              <details style={{ marginTop: '10px', fontSize: '0.85rem' }}>
                                <summary style={{ cursor: 'pointer', color: '#666' }}>View Raw Image URLs</summary>
                                <div style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', marginTop: '5px' }}>
                                  {product.images.map((img, idx) => (
                                    <div key={idx} style={{ marginBottom: '5px', wordBreak: 'break-all' }}>
                                      <strong>Image {idx + 1}:</strong><br />
                                      <code style={{ fontSize: '0.8rem' }}>{img}</code>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>No products submitted</p>
                  )}
                </div>

                {/* Documents */}
                <div className="details-section">
                  <h3>Documents ({selectedApplication.documents?.length || 0})</h3>
                  {selectedApplication.documents && selectedApplication.documents.length > 0 ? (
                    <ul className="documents-list">
                      {selectedApplication.documents.map((doc, idx) => (
                        <li key={idx}>
                          <strong>{doc.type}:</strong>{' '}
                          <a href={doc.url} target="_blank" rel="noopener noreferrer" download>
                            Download {doc.fileName || `Document ${idx + 1}`}
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No documents uploaded</p>
                  )}
                </div>
                  </>
                )}

                {/* Images Tab */}
                {activeTab === 'images' && (
                  <div className="images-gallery-tab">
                    {selectedApplication.products && selectedApplication.products.some(p => (p.images?.length > 0 || p.imageUrls?.length > 0)) ? (
                      <div className="all-images-grid">
                        {selectedApplication.products.map((product, prodIdx) => {
                          const imgs = [...(product.imageUrls || []), ...(product.images || [])];
                          return imgs.length > 0 ? (
                            <div key={prodIdx} className="product-images-section">
                              <h4 style={{ marginBottom: '15px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '8px' }}>
                                {product.name} ({imgs.length} {imgs.length === 1 ? 'image' : 'images'})
                              </h4>
                              <div className="images-grid-gallery">
                                {imgs.map((img, imgIdx) => (
                                  <div key={imgIdx} className="image-card" onClick={() => handleViewVendorImage(img, product.name, selectedApplication.applicationNumber || selectedApplication.referenceNumber)}>
                                    <img src={img} alt={`${product.name} ${imgIdx + 1}`} />
                                    <div className="image-details">
                                      <p><strong>Image {imgIdx + 1}</strong></p>
                                      <p className="image-url-preview">{img.substring(0, 50)}...</p>
                                      <button className="btn-sm btn-primary" onClick={(e) => {e.stopPropagation(); handleViewVendorImage(img, product.name, selectedApplication.applicationNumber || selectedApplication.referenceNumber)}}>
                                        View Full Size
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      <p style={{ textAlign: 'center', color: '#999', padding: '40px' }}>No images found in this application</p>
                    )}
                  </div>
                )}

                {/* Comments Tab */}
                {activeTab === 'comments' && (
                  <div className="comments-tab">
                    <Comments entityType="vendor" entityId={selectedApplication._id} />
                  </div>
                )}
              </div>
            ) : (
              <p>No details available</p>
            )}

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeDetailsModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Image Viewer Modal */}
      {showVendorImageViewer && selectedVendorImage && (
        <div className="modal-overlay" onClick={closeVendorImageViewer}>
          <div className="modal-content image-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeVendorImageViewer}>×</button>
            <h2>Image Viewer</h2>
            
            <div className="image-viewer-container">
              <img 
                src={selectedVendorImage.url} 
                alt={selectedVendorImage.alt || 'Vendor product image'}
                className="full-size-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiNmMGYwZjAiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjI0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2UgTm90IEF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=';
                }}
              />
            </div>

            <div className="image-properties">
              <h3>Image Properties</h3>
              <table className="properties-table">
                <tbody>
                  <tr>
                    <td><strong>Product:</strong></td>
                    <td>{selectedVendorImage.productName || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Application #:</strong></td>
                    <td>{selectedVendorImage.appNumber || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>URL:</strong></td>
                    <td style={{ wordBreak: 'break-all', fontSize: '0.9em' }}>
                      {selectedVendorImage.url}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Alt Text:</strong></td>
                    <td>{selectedVendorImage.alt || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleDownloadVendorImage}>
                Download Image
              </button>
              <button className="btn btn-secondary" onClick={closeVendorImageViewer}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminVendors;
